import React, { createContext, useContext, PropsWithChildren } from 'react';
import { GeoTargetingData, Website, CampaignFields, CampaignFormInputs, TargetingGuidanceConfig } from 'types';
import { useCampaignForm } from 'hooks';
import { Path } from 'react-hook-form';
import { defaultTargetingGuidanceConfig } from 'shared';

// Context state type
type TargetingConfigContextType = {
  gender: string[];
  setGender: (value: TargetingGuidanceConfig['gender']) => void;
  age: string[];
  setAge: (value: TargetingGuidanceConfig['age']) => void;
  languages: string[];
  setLanguages: (value: TargetingGuidanceConfig['languages']) => void;
  locations: GeoTargetingData[];
  setLocations: (value: TargetingGuidanceConfig['locations']) => void;
  interests: string[];
  setInterests: (value: TargetingGuidanceConfig['interests']) => void;
  industry: string[];
  setIndustry: (value: TargetingGuidanceConfig['industry']) => void;
  companySize: string[];
  setCompanySize: (value: TargetingGuidanceConfig['companySize']) => void;
  resetToDefaults: () => void;
};

const TargetingConfigContext = createContext<TargetingConfigContextType | undefined>(undefined);

export const useTargetingConfigContext = () => {
  const ctx = useContext(TargetingConfigContext);
  if (!ctx) throw new Error('useTargetingConfigContext must be used within TargetingConfigProvider');
  return ctx;
};

// Helper function to count selections for each section
export const useTargetingSelectionCounts = () => {
  const { gender, age, languages, locations, industry, companySize } = useTargetingConfigContext();

  return {
    sociodemographicCount: gender.length + age.length + languages.length,
    locationsCount: locations.length,
    b2bCount: industry.length + companySize.length,
  };
};

type TargetingConfigProviderProps = {
  website?: Website;
  websiteIndex?: number;
};

export const TargetingConfigProvider = ({
  children,
  websiteIndex,
}: PropsWithChildren<TargetingConfigProviderProps>) => {
  const { setValue, onFieldChange, watch } = useCampaignForm();

  let guidanceConfig: TargetingGuidanceConfig =
    websiteIndex !== undefined
      ? watch(`websites.${websiteIndex}.targetingGuidanceConfig`)
      : watch('targetingGuidanceConfig');

  // guidanceConfig should always be an object now, but provide fallback just in case
  if (!guidanceConfig || typeof guidanceConfig !== 'object') {
    guidanceConfig = defaultTargetingGuidanceConfig;
  }

  const targetingConfig = {
    gender: guidanceConfig.gender || [],
    age: guidanceConfig.age || [],
    languages: guidanceConfig.languages || [],
    interests: guidanceConfig.interests || [],
    locations: guidanceConfig.locations || [],
    industry: guidanceConfig.industry || [],
    companySize: guidanceConfig.companySize || [],
  };

  const getSetterFor = (option: keyof TargetingGuidanceConfig) => (optionValue: string[]) => {
    if (websiteIndex !== undefined) {
      setValue(
        `${CampaignFields.websites}.${websiteIndex}.targetingGuidanceConfig` as Path<CampaignFormInputs>,
        {
          ...targetingConfig,
          [option]: optionValue,
        } as unknown as string
      );
      onFieldChange(CampaignFields.websites);
    } else {
      setValue(CampaignFields.targetingGuidanceConfig, { ...targetingConfig, [option]: optionValue });
      onFieldChange(CampaignFields.targetingGuidanceConfig);
    }
  };

  const resetToDefaults = () => {
    if (websiteIndex !== undefined) {
      setValue(
        `${CampaignFields.websites}.${websiteIndex}.targetingGuidanceConfig` as Path<CampaignFormInputs>,
        defaultTargetingGuidanceConfig as unknown as string
      );
      onFieldChange(CampaignFields.websites);
    } else {
      setValue(CampaignFields.targetingGuidanceConfig, defaultTargetingGuidanceConfig);
      onFieldChange(CampaignFields.targetingGuidanceConfig);
    }
  };

  return (
    <TargetingConfigContext.Provider
      value={{
        gender: targetingConfig.gender,
        setGender: getSetterFor('gender'),
        age: targetingConfig.age,
        setAge: getSetterFor('age'),
        languages: targetingConfig.languages,
        setLanguages: getSetterFor('languages'),
        locations: targetingConfig.locations,
        setLocations: getSetterFor('locations') as unknown as (value: TargetingGuidanceConfig['locations']) => void,
        interests: targetingConfig.interests,
        setInterests: getSetterFor('interests'),
        industry: targetingConfig.industry,
        setIndustry: getSetterFor('industry'),
        companySize: targetingConfig.companySize,
        setCompanySize: getSetterFor('companySize'),
        resetToDefaults,
      }}
    >
      {children}
    </TargetingConfigContext.Provider>
  );
};
